/*
 * @providesModule NavigationBar
 * 
 *
 * A Component that draws a navigation bar. Place it before other components so it appears
 * at the top of the screen. The component will use the current theme for style information
 * which you may override.
 *
 *  Example:
 *
 *  <NavigationBar
 *        titleImage={'medal_place_1_skz.png'}
 *        onBackButtonPress={()=>{ }} // pop the current view
 *        titleImageSize={{width:32, height:32}}
 *        title={I18n.message("Welcome")}/>
 *
 */

import React from 'react';

import Label from 'Label';
import Optional from 'Optional';
import PropTypes from 'prop-types';
import PushButton from 'PushButton';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import Sizes from 'Sizes';

import {
  StatusBar,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';

const {
  fontPrimary,
  fontSecondary,
  textColorPrimary,
  viewPrimaryColor,
  viewCornerRadiusPrimary,
  btnGradientOne,
  btnTextColorOne,
} = SkillzStyle.fullStyles;

const IOS_NAV_BAR_HEIGHT = Sizes.orientation({
  portrait: 44,
  landscape: Sizes.select({
    tablet: 44,
    phone: 32,
  }),
});

const IOS_STATUS_BAR_HEIGHT = 20;
const IOS_NAV_BAR_PADDING = 8;
const TEXT_COLOR = textColorPrimary.color;
const TEXT_FONT_FACE = fontPrimary.fontFamily;
const BACKGROUND_COLOR = viewPrimaryColor.backgroundColor;
const TITLE_IMAGE_PADDING = 2;

const FONT_SIZE = Sizes.orientation({
  portrait: 16,
  landscape: Sizes.select({
    phone: 16,
    tablet: 17,
  }),
});

export default class NavigationBar extends React.Component {
  static propTypes = {
    accessibilityLabel: PropTypes.string,
    backButtonColor: PropTypes.string,
    backButtonStyle: PropTypes.object,
    backButtonTestID: PropTypes.string,
    backgroundColor: PropTypes.string,
    hasBackButton: PropTypes.bool,
    hasRightButton: PropTypes.bool,
    hasRightComponent: PropTypes.bool,
    onBackButtonPress: PropTypes.func,
    onRightButtonPress: PropTypes.func,
    onTitlePress: PropTypes.func,
    renderRightComponent: PropTypes.object,
    rightButtonColor: PropTypes.arrayOf(PropTypes.string),
    rightButtonEnabled: PropTypes.bool,
    rightButtonTitle: PropTypes.string,
    rightButtonTestID: PropTypes.string,
    shouldTintTitleImage: PropTypes.bool,
    showingStatusBarTranslucentAndroid: PropTypes.bool,
    subTitle: PropTypes.string,
    subTitleStyle: PropTypes.any,
    subTitleTestID: PropTypes.string,
    testID: PropTypes.string,
    title: PropTypes.string,
    titleButtonTestID: PropTypes.string,
    titleImageOnRight: PropTypes.bool,
    titleImage: PropTypes.string,
    titleImageAccessibility: PropTypes.string,
    titleImageSize: PropTypes.shape({width: PropTypes.number, height: PropTypes.number}),
    titleStyle: PropTypes.any,
    titleSuffix: PropTypes.string,
    titleSuffixStyle: PropTypes.any,
    titleTestID: PropTypes.string,
    hideBottomBorder: PropTypes.bool,
  };

  _renderRightButton(): React.ReactNode | null {
    if (this.props.hasRightButton) {
      const buttonTestID = this.props.rightButtonTestID ? this.props.rightButtonTestID : this.props.rightButtonTitle;
      return (
        <PushButton onPress={this.props.onRightButtonPress}
                    cornerRadius={viewCornerRadiusPrimary.borderRadius}
                    colors={this.props.rightButtonColor}
                    enabled={this.props.rightButtonEnabled}
                    style={[styles.chatButtonView, styles.menuView]}
                    title={this.props.rightButtonTitle}
                    textStyle={styles.rightButtonLabel}
                    accessible={true}
                    accessibilityRole={'button'}
                    testID={buttonTestID + '_button'}/>
      );

    } else if (this.props.hasRightComponent) {
      return (
        <View style={[styles.menuView, {alignSelf: 'center', justifyContent: 'center'}]}>
          {this.props.renderRightComponent}
        </View>
      );
    }
  }

  _renderBackButton(): React.ReactNode | null {
    if (!this.props.hasBackButton) {
      return null;
    }

    return (
      <View style={{...styles.buttonView, ...this.props?.backButtonStyle}}>
        <TouchableOpacity style={styles.backButtonTouchable}
                          onPress={this.props.onBackButtonPress}
                          hitSlop={{top: 6, left: 14, bottom: 6, right: 60}}
                          testID={this.props.backButtonTestID}
                          accessible={false}>
          <View style={styles.backButtonContent}
                accessible={true}
                accessibilityLabel={'back_button'}
                accessibilityRole={'button'}>
            <SkillzImage source={'ic_chevron_left_skz.png'}
                         style={{width: this.props?.backButtonStyle?.height || 50, height: this.props?.backButtonStyle?.height || 50}}
                         resizeMode={'stretch'}
                         tintColor={this.props.backButtonColor || TEXT_COLOR}/>
          </View>
        </TouchableOpacity>
      </View>
    );
  }

  _renderTitleImage(): React.ReactNode {
    const iconStyle = this.props.titleImageOnRight ? styles.titleIconRight : styles.titleIconLeft;
    const paddingStyle = this.props.titleImageOnRight ? {marginLeft: TITLE_IMAGE_PADDING} : {marginRight: TITLE_IMAGE_PADDING};
    const imageProps = {};
    if (this.props.shouldTintTitleImage) {
      imageProps.color = textColorPrimary.color;
    }
    return (
      <View style={[{maxWidth: this.props.titleImageSize.width + TITLE_IMAGE_PADDING}, paddingStyle]}
            accessibilityLabel={this.props.titleImageAccessibility}
            accessible={true}>
        <SkillzImage resizeMode={'contain'}
                     source={this.props.titleImage}
                     style={[this.props.titleImageSize, iconStyle]}
                     {...imageProps}/>
      </View>
    );
  }

  _renderTitleContent(): React.ReactNode {
    return (
      <View style={styles.titleViewInnerContainer}>
        <View style={styles.titleViewTextContainer}>
          <Optional test={this.props.titleImage && !this.props.titleImageOnRight}>
            {this._renderTitleImage()}
          </Optional>
          <View style={styles.titleContainer}>
            <Label style={[styles.titleTextLabel, styles.titleText, this.props.titleStyle]}
                   numberOfLines={1}
                   adjustsFontSizeToFit={false}
                   accessible
                   accessibilityLabel={this.props.title + '_title'}>
              {this.props.title}
            </Label>
            <Label style={[styles.titleTextLabel, styles.titleText, this.props.titleSuffixStyle]}
                   accessible
                   accessibilityLabel={this.props.titleSuffix + '_titleSuffix'}>
              {this.props.titleSuffix}
            </Label>
          </View>
          <Optional test={this.props.titleImage && this.props.titleImageOnRight}>
            {this._renderTitleImage()}
          </Optional>
        </View>
        <Optional test={this.props.subTitle && this.props.subTitle.length > 0}>
          <Label style={[styles.subTitleTextLabel, this.props.subTitleStyle]}
                 key={this.props.subTitle}
                 accessible={true}
                 testID={this.props.subTitleTestID}
                 accessibilityLabel={this.props.subTitle}>
            {this.props.subTitle}
          </Label>
        </Optional>
      </View>
    );
  }

  _renderTitle(): React.ReactNode | null {
    if (!this.props.title) {
      return null;
    }

    if (this.props.onTitlePress) {
      const testID = this.props.testID ? `${this.props.testID}_title` : undefined;
      return (
        <TouchableOpacity testID={testID}
                          style={styles.titleView}
                          onPress={this.props.onTitlePress}
                          testID= {this.props.titleButtonTestID}
                          accessible={false}>
          <View style={styles.titleView}
                testID={this.props.titleTestID}>
            {this._renderTitleContent()}
          </View>
        </TouchableOpacity>
      );
    }

    return (
      <View style={styles.titleView}>
        {this._renderTitleContent()}
      </View>
    );
  }

  render(): React.ReactNode {
    const statusBarPadding = this.props.showingStatusBarTranslucentAndroid ? {paddingTop: StatusBar.currentHeight} : {};
    return (
      <View style={statusBarPadding}>
        <View style={[styles.navigationBar, {backgroundColor: this.props.backgroundColor || BACKGROUND_COLOR}, this.props.hideBottomBorder ? {} : styles.navigationBarBorder]}
              accessibilityLabel={this.props.accessibilityLabel}
              testID={this.props.testID}>
          {this._renderTitle()}
          {this._renderBackButton()}
          {this._renderRightButton()}
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  backButtonContent: {
    alignItems: 'center',
    flexDirection: 'row',
    marginLeft: -12,
  },

  backButtonTouchable: {
    flexDirection: 'row',
    flexGrow: 1,
  },

  buttonView: {
    height: IOS_NAV_BAR_HEIGHT,
    justifyContent: 'center',
    position: 'absolute',
    left: 14,
  },

  chatButtonView: {
    height: 28,
    width: 58,
    alignSelf: 'center',
    justifyContent: 'center',
  },

  rightButtonLabel: {
    ...btnTextColorOne,
    ...fontSecondary,
    fontSize: 12,
  },

  menuView: {
    position: 'absolute',
    right: 6,
  },

  navigationBarBorder: {
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    borderBottomWidth: 1,
  },

  navigationBar: {
    backgroundColor: BACKGROUND_COLOR,
    flexDirection: 'row',
    height: IOS_NAV_BAR_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: IOS_NAV_BAR_PADDING,
  },

  statusBar: {
    backgroundColor: SkillzStyle.fullStyles.statusBarViewBackgroundColor.backgroundColor,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    borderBottomWidth: 1,
    height: IOS_STATUS_BAR_HEIGHT,
  },

  titleIconRight: {
    alignSelf: 'center',
    marginHorizontal: 5,
    marginTop: 2,
  },

  titleIconLeft: {
    alignSelf: 'center',
    marginRight: 5,
    marginTop: 2,
  },

  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  titleText: {
    ...fontSecondary,
  },

  titleTextLabel: {
    color: TEXT_COLOR,
    fontFamily: TEXT_FONT_FACE,
    fontSize: FONT_SIZE,
  },

  subTitleTextLabel: {
    marginTop: -2,
    ...fontPrimary,
    fontSize: 14,
    ...textColorPrimary,
  },

  titleView: {
    alignItems: 'center',
    flexDirection: 'row',
    height: IOS_NAV_BAR_HEIGHT,
    justifyContent: 'center',
    alignSelf: 'center',
  },

  titleViewTextContainer: {
    alignItems: 'center',
    flex: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignSelf: 'center',
    marginTop: 2,
  },

  titleViewInnerContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    alignSelf: 'center',
  },

});

NavigationBar.defaultProps = {
  hasBackButton: true,
  hasRightButton: false,
  hasRightComponent: false,
  onBackButtonPress: null,
  titleImageSize: {width: 0, height: 0},
  onTitlePress: null,
  rightButtonColor: btnGradientOne,
  backgroundColor: null,
  backButtonColor: null,
};

export const NavBarHeight = IOS_NAV_BAR_HEIGHT;
